<template>
    <div class="ak-report">
      <div class="ak-scroll">
        <table class="ak-table">
          <colgroup>
            <col class="ak-col-label">
            <col
              v-for="col in columns"
              :key="`col-${col.key}`"
              class="ak-col-num">
          </colgroup>
          <thead>
            <tr>
              <th class="ak-th ak-th--label">Keterangan</th>
              <th
                v-for="col in columns"
                :key="`th-${col.key}`"
                class="ak-th">
                {{ col.label }}
              </th>
            </tr>
          </thead>
  
          <tbody>
            <tr
              v-for="row in rows"
              :key="row.id"
              :class="rowClass(row)"
              @click="selectRow(row)">
              <td class="ak-td ak-td--label">
                {{ row.label }}
              </td>
              <td
                v-for="col in columns"
                :key="`${row.id}-${col.key}`"
                class="ak-td ak-num">
                {{ cellValue(row, col) }}
              </td>
            </tr>
  
            <tr v-if="rows.length === 0">
              <td class="ak-td ak-td--empty" :colspan="1 + columns.length">
                Belum Ada Data Arus Kas
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </template>
  
  <script>
    // Baris yang ditampilkan tebal (bold) di seluruh kolom.
    const BOLD_ROW_TYPES = ['header', 'formula', 'total', 'subtotal']
  
    // Baris yang hanya menampilkan label (judul section / catatan),
    // tanpa nilai sama sekali di kolom manapun.
    const TEXT_ONLY_ROW_TYPES = ['header', 'note']
  
    export default {
      name: 'ArusKasReportTable',
  
      props: {
        rows: {
          type: Array,
          default: () => []
        },
        // Diisi dari `results.data.columns` API, contoh:
        // [
        //   { key: 'saldo_bulan_sebelumnya', label: 'Saldo bulan sebelumnya' },
        //   { key: 'saldo_bulan', label: 'Saldo s.d. 2026-09-30' },
        //   { key: 'amount', label: 'Jumlah' }
        // ]
        columns: {
          type: Array,
          default: () => []
        }
      },

      data () {
        return {
          selectedRowId: null
        }
      },
  
      methods: {
        isTextOnly (row) {
          return TEXT_ONLY_ROW_TYPES.indexOf(row.row_type) !== -1
        },
  
        isBold (row) {
          return BOLD_ROW_TYPES.indexOf(row.row_type) !== -1
        },

        selectRow (row) {
          this.selectedRowId = row.id
        },
  
        rowClass (row) {
          return [
            'ak-row',
            `ak-row--${row.row_type || 'item'}`,
            this.isBold(row) ? 'ak-row--bold' : null,
            this.selectedRowId === row.id ? 'ak-row--selected' : null
          ]
        },
  
        // Aturan tampil nilai per kolom:
        // - baris header/note -> selalu kosong (cuma judul/catatan)
        // - kolom 'amount' -> selalu tampil (termasuk 0)
        // - kolom saldo lain -> tampil hanya kalau row.show_saldo true
        cellValue (row, col) {
          if (this.isTextOnly(row)) return ''
  
          if (col.key !== 'amount' && !row.show_saldo) return ''
  
          return this.formatNumber(row[col.key])
        },
  
        // null/undefined -> sel dikosongkan. Nilai 0 tetap tampil sebagai "0".
        formatNumber (value) {
          if (value === null || value === undefined || value === '') return ''
  
          let num = Number(value)
          if (isNaN(num)) return ''
  
          let negative = num < 0
          let intVal = Math.round(Math.abs(num))
  
          let reversed = intVal.toString().split('').reverse().join('')
          let grouped = []
  
          for (let i = 0; i < reversed.length; i += 3) {
            grouped.push(reversed.substr(i, 3))
          }
  
          let formatted = grouped.join('.').split('').reverse().join('')
  
          return negative ? `-${formatted}` : formatted
        }
      }
    }
  </script>
  
  <style scoped>
    .ak-report {
      --ak-border: #e0e3e8;
      --ak-bg: #ffffff;
      --ak-bg-head: #f3f4f6;
      --ak-head-text: #374151;
      width: 100%;
    }
  
    .ak-scroll {
      overflow-x: auto;
      width: 100%;
      border: 1px solid var(--ak-border);
      border-radius: 8px;
      background: var(--ak-bg);
      -webkit-overflow-scrolling: touch;
    }
  
    .ak-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;
      font-size: 12px;
      font-weight: 400;
      line-height: 1.45;
      color: #111827;
      background: var(--ak-bg);
    }
  
    .ak-col-label {
      width: auto;
    }
  
    .ak-col-num {
      width: 190px;
    }
  
    .ak-th,
    .ak-td {
      box-sizing: border-box;
      border-bottom: 1px solid var(--ak-border);
      border-right: 1px solid var(--ak-border);
      padding: 7px 10px;
      vertical-align: middle;
    }
  
    .ak-th:last-child,
    .ak-td:last-child {
      border-right: none;
    }
  
    .ak-th {
      font-size: 11px;
      font-weight: 600;
      line-height: 1.3;
      letter-spacing: 0.01em;
      text-align: center;
      background: var(--ak-bg-head);
      color: var(--ak-head-text);
    }
  
    .ak-th--label {
      text-align: center;
    }
  
    .ak-td--label {
      text-align: left;
      word-break: break-word;
    }
  
    .ak-num {
      text-align: right;
      font-variant-numeric: tabular-nums;
      font-feature-settings: "tnum";
      letter-spacing: 0.01em;
      white-space: nowrap;
    }
  
    .ak-row {
      cursor: pointer;
    }

    .ak-row:hover .ak-td,
    .ak-row--selected .ak-td {
      background-color: #1976d236;
    }

    .ak-row--item .ak-td {
      font-weight: 400;
    }
  
    .ak-row--header .ak-td {
      font-size: 12px;
      font-weight: 700;
    }
  
    .ak-row--formula .ak-td,
    .ak-row--subtotal .ak-td {
      font-weight: 600;
    }
  
    .ak-row--total .ak-td,
    .ak-row--bold.ak-row--total .ak-td,
    .ak-row--bold.ak-row--header .ak-td {
      font-weight: 700;
    }
  
    .ak-row--bold.ak-row--formula .ak-td,
    .ak-row--bold.ak-row--subtotal .ak-td {
      font-weight: 600;
    }
  
    .ak-row--note .ak-td {
      font-size: 11px;
      font-weight: 400;
      font-style: italic;
      color: #6b7280;
    }
  
    .ak-td--empty {
      text-align: center;
      padding: 24px 10px;
      font-size: 12px;
      font-weight: 400;
      color: #6b7280;
      font-style: italic;
    }
  </style>